/* eslint-disable no-unsafe-optional-chaining */
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import { toTrainingCourseState } from '@data/dto/training-course-state.dto';
import { Meta, PaginateResult } from '@data/models/api.interface';
import {
  TrainingCourseState,
  TrainingCourseStateHttp,
  TrainingCourseStateWizard,
} from '@data/models/training-course-state.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class TrainingCourseStateService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly trainingCourseEndpoint = '/training-courses';
  private readonly trainingCourseStateEndpoint = '/training-course-states';

  private errorService = container.get<ErrorService>(ErrorService);

  public async listTrainingCourseStatesByTrainingCourse(
    trainingCourseId: string,
  ): Promise<PaginateResult<TrainingCourseState[]>> {
    const { data: response, error } = await this.API?.get<{
      data: TrainingCourseStateHttp[];
      meta: Meta;
    }>(`${this.trainingCourseEndpoint}/${trainingCourseId}/states`);

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    return {
      items: response?.data?.map(toTrainingCourseState) || [],
      totalSize: response?.meta?.count || 0,
    };
  }

  public async toggleTrainingCourseState(
    state: TrainingCourseState,
  ): Promise<TrainingCourseState | null> {
    const endpoint = `${this.trainingCourseStateEndpoint}/${state.id}/${
      state.checked ? 'uncheck' : 'check'
    }`;

    const { data, error } = await this.API?.patch<
      { data: TrainingCourseState },
      { data: TrainingCourseStateHttp }
    >(endpoint, {
      data: state,
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return null;
    }

    return toTrainingCourseState(data?.data || {});
  }

  public async upsertTrainingCourseState({
    id,
    ...state
  }: TrainingCourseStateWizard): Promise<TrainingCourseState | null> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<TrainingCourseStateWizard, 'id'> },
        { data: TrainingCourseStateHttp }
      >(this.trainingCourseStateEndpoint, { data: state });

      if (error) {
        this.errorService.handleHttpError(error);
        return null;
      }

      return toTrainingCourseState(data?.data || {});
    }

    const { data, error } = await this.API?.patch<
      { data: TrainingCourseStateWizard },
      { data: TrainingCourseStateHttp }
    >(`${this.trainingCourseStateEndpoint}/${id}`, {
      data: { ...state, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return null;
    }

    return toTrainingCourseState(data?.data || {});
  }

  public async deleteTrainingCourseState({
    id,
  }: TrainingCourseState): Promise<void> {
    this.API?.delete(`${this.trainingCourseStateEndpoint}/${id}`);
    return;
  }
}
